/**
 * AI Data Access Settings Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * Controls what data the AI can access.
 */

import React from 'react';
import { Users, Briefcase, Mic, FileText, AlertTriangle } from 'lucide-react';
import type { AISettingsUpdateData } from '../../../types/admin';

interface AIDataAccessSettingsProps {
  formData: AISettingsUpdateData;
  onChange: (updates: Partial<AISettingsUpdateData>) => void;
}

const DATA_ACCESS_OPTIONS = [
  {
    key: 'ai_can_access_client_data' as const,
    label: 'Client Data',
    description: 'Basic client information (names, contact details)',
    icon: Users,
    risk: 'low',
  },
  {
    key: 'ai_can_access_portfolio_data' as const,
    label: 'Portfolio Data',
    description: 'Investment holdings and performance data',
    icon: Briefcase,
    risk: 'medium',
  },
  {
    key: 'ai_can_access_meeting_transcripts' as const,
    label: 'Meeting Transcripts',
    description: 'Transcribed meeting conversations',
    icon: Mic,
    risk: 'medium',
  },
  {
    key: 'ai_can_access_documents' as const,
    label: 'Documents',
    description: 'Uploaded documents and files',
    icon: FileText,
    risk: 'medium',
  },
];

export const AIDataAccessSettings: React.FC<AIDataAccessSettingsProps> = ({
  formData,
  onChange,
}) => {
  const sensitiveAccessEnabled = formData.ai_sensitive_data_access ?? false;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Data Access Controls</h3>
        <p className="text-sm text-gray-500 mt-1">
          Configure which types of data the AI assistant can access. Follow the principle of least
          privilege.
        </p>
      </div>

      {/* Standard Data Access */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">Standard Data Access</h4>
        {DATA_ACCESS_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isEnabled = formData[option.key] ?? true;

          return (
            <label
              key={option.key}
              className={`
                flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors
                ${isEnabled ? 'border-[#002B45] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isEnabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <Icon size={18} className={isEnabled ? 'text-[#002B45]' : 'text-gray-400'} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{option.label}</span>
                    {option.risk === 'medium' && (
                      <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded">
                        Medium Risk
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => onChange({ [option.key]: e.target.checked })}
                className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
              />
            </label>
          );
        })}
      </div>

      {/* Sensitive Data Access - High Risk */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Sensitive Data Access</h4>
        <label
          className={`
            flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-colors
            ${sensitiveAccessEnabled ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'}
          `}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${sensitiveAccessEnabled ? 'bg-red-100' : 'bg-gray-100'}`}
            >
              <AlertTriangle
                size={18}
                className={sensitiveAccessEnabled ? 'text-red-600' : 'text-gray-400'}
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">Sensitive Personal Data</span>
                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded font-medium">
                  HIGH RISK
                </span>
              </div>
              <p className="text-sm text-gray-500">
                SSN, passport numbers, tax IDs, and other highly sensitive identifiers
              </p>
            </div>
          </div>
          <input
            type="checkbox"
            checked={sensitiveAccessEnabled}
            onChange={(e) => onChange({ ai_sensitive_data_access: e.target.checked })}
            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
          />
        </label>

        {sensitiveAccessEnabled && (
          <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium">Warning: High-Risk Data Access Enabled</p>
                <ul className="mt-1 list-disc list-inside">
                  <li>AI will have access to SSN, passport numbers, tax IDs</li>
                  <li>May violate GDPR data minimization principles</li>
                  <li>Ensure PII masking is properly configured</li>
                  <li>Review your data protection impact assessment</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>GDPR Principle:</strong> Data minimization requires collecting and processing only
          the personal data strictly necessary for the purpose. Disable access to data the AI
          doesn't need to perform its functions.
        </p>
      </div>
    </div>
  );
};

export default AIDataAccessSettings;
